
import { supabase } from '@/lib/supabase';

// Define types for the data returned from the database
export interface CategoryCount {
  category: string;
  count: number;
}

export interface DifficultyCount {
  difficulty: string;
  count: number;
}

export interface CategoryDifficultyDistribution {
  category: string;
  easy: number;
  medium: number;
  hard: number;
  total: number;
  easy_percent: number;
  medium_percent: number;
  hard_percent: number;
}

// Function to get question statistics by category and difficulty
export const getQuestionStats = async () => {
  try {
    // Get questions by category
    const { data: categoryData, error: categoryError } = await supabase
      .rpc('get_distinct_categories');
      
    if (categoryError) {
      console.error('Error fetching categories:', categoryError);
      return { categories: [], difficulties: [] };
    }
    
    // Count questions by category
    const categories: CategoryCount[] = [];
    
    for (const cat of categoryData || []) {
      const { count, error } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('Category', cat.category);
        
      if (error) {
        console.error(`Error counting questions for ${cat.category}:`, error);
      } else {
        categories.push({
          category: cat.category,
          count: count || 0
        });
      }
    }
    
    // Count questions by difficulty
    // Fix: Remove grouping and do filter+count instead
    const difficulties: DifficultyCount[] = [];
    
    // Get distinct difficulties and count each one separately
    for (const diff of ['easy', 'medium', 'hard']) {
      const { count, error: countError } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('Difficulty', diff);
          
      if (countError) {
        console.error(`Error counting ${diff} questions:`, countError);
      } else {
        difficulties.push({
          difficulty: diff,
          count: count || 0
        });
      }
    }
    
    return { categories, difficulties };
  } catch (error) {
    console.error('Error in getQuestionStats:', error);
    return { categories: [], difficulties: [] };
  }
};

// Function to get difficulty summary across all questions
export const getQuestionDifficultySummary = async () => {
  try {
    // Get counts for each difficulty
    const { count: easyCount, error: easyError } = await supabase
      .from('questions')
      .select('*', { count: 'exact', head: true })
      .eq('Difficulty', 'easy');
      
    const { count: mediumCount, error: mediumError } = await supabase
      .from('questions')
      .select('*', { count: 'exact', head: true })
      .eq('Difficulty', 'medium');
      
    const { count: hardCount, error: hardError } = await supabase
      .from('questions')
      .select('*', { count: 'exact', head: true })
      .eq('Difficulty', 'hard');
      
    const { count: totalCount, error: totalError } = await supabase
      .from('questions')
      .select('*', { count: 'exact', head: true });
      
    if (easyError || mediumError || hardError || totalError) {
      console.error('Error fetching difficulty summary:', easyError || mediumError || hardError || totalError);
      return null;
    }
    
    return {
      total: totalCount || 0,
      hard: hardCount || 0,
      medium: mediumCount || 0,
      easy: easyCount || 0
    };
  } catch (error) {
    console.error('Error in getQuestionDifficultySummary:', error);
    return null;
  }
};

// Function to get difficulty distribution by category
export const getDifficultyByCategory = async () => {
  try {
    // Since we can't use the function with group issue, 
    // let's do it by fetching categories and then getting stats for each
    const { data: categories, error: catError } = await supabase
      .rpc('get_distinct_categories');
      
    if (catError) {
      console.error('Error fetching categories:', catError);
      return [];
    }
    
    const result: CategoryDifficultyDistribution[] = [];
    
    for (const catObj of categories || []) {
      const category = catObj.category;
      
      // Get counts for each difficulty in this category
      const { count: easyCount, error: easyError } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('Category', category)
        .eq('Difficulty', 'easy');
        
      const { count: mediumCount, error: mediumError } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('Category', category)
        .eq('Difficulty', 'medium');
        
      const { count: hardCount, error: hardError } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('Category', category)
        .eq('Difficulty', 'hard');
        
      const { count: totalCount, error: totalError } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('Category', category);
        
      if (easyError || mediumError || hardError || totalError) {
        console.error(`Error fetching counts for category ${category}:`, 
          easyError || mediumError || hardError || totalError);
        continue;
      }
      
      const total = totalCount || 0;
      const easy = easyCount || 0;
      const medium = mediumCount || 0;
      const hard = hardCount || 0;
      
      result.push({
        category,
        total,
        easy,
        medium,
        hard,
        easy_percent: total > 0 ? (easy / total) * 100 : 0,
        medium_percent: total > 0 ? (medium / total) * 100 : 0,
        hard_percent: total > 0 ? (hard / total) * 100 : 0
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error in getDifficultyByCategory:', error);
    return [];
  }
};

// Function to reset all question difficulties
export const resetQuestionDifficulty = async () => {
  try {
    const { data, error } = await supabase
      .rpc('reset_all_question_difficulties');
      
    if (error) {
      console.error('Error resetting question difficulties:', error);
      return { error: error.message };
    }
    
    return { reset: data || 0 };
  } catch (error) {
    console.error('Error in resetQuestionDifficulty:', error);
    return { error: 'An unexpected error occurred' };
  }
};

// Function to update question difficulty
export const updateQuestionDifficulty = async () => {
  try {
    // Get all categories
    const { data: categoryData, error: categoryError } = await supabase
      .rpc('get_distinct_categories');
      
    if (categoryError) {
      console.error('Error fetching categories:', categoryError);
      return { error: categoryError.message };
    }
    
    let totalUpdated = 0;
    const categories = categoryData || [];
    
    // Update each category one by one
    for (const cat of categories) {
      console.log(`Updating difficulty for category: ${cat.category}`);
      
      const { data, error } = await supabase
        .rpc('update_question_difficulty_by_category', {
          category_name: cat.category
        });
        
      if (error) {
        console.error(`Error updating difficulty for ${cat.category}:`, error);
        continue;
      }
      
      totalUpdated += data || 0;
      console.log(`Updated ${data} questions in ${cat.category}`);
    }
    
    return { updated: totalUpdated };
  } catch (error) {
    console.error('Error in updateQuestionDifficulty:', error);
    return { error: 'An unexpected error occurred' };
  }
};

// Function to update question difficulty for a specific category
export const updateCategoryDifficulty = async (category: string) => {
  try {
    const { data, error } = await supabase
      .rpc('update_question_difficulty_by_category', {
        category_name: category
      });
      
    if (error) {
      console.error(`Error updating difficulty for ${category}:`, error);
      return { error: error.message };
    }
    
    return { updated: data || 0 };
  } catch (error) {
    console.error(`Error in updateCategoryDifficulty for ${category}:`, error);
    return { error: 'An unexpected error occurred' };
  }
};

export const countQuestionsBySource = async () => {
  try {
    // Instead of using group, which is not available, use an RPC function or separate counts
    const { data, error } = await supabase
      .from('questions')
      .select('Source')
      .not('Source', 'is', null);

    if (error) {
      console.error('Error counting questions by source:', error);
      return { error: error.message };
    }

    // Count occurrences manually
    const sourceCounts = data.reduce((acc: Record<string, number>, item) => {
      const source = item.Source || 'Unknown';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {});

    // Convert to array of objects
    const sources = Object.entries(sourceCounts).map(([source, count]) => ({
      Source: source,
      count
    }));

    const { count: totalCount, error: totalError } = await supabase
      .from('questions')
      .select('count(*)', { count: 'exact' });

    if (totalError) {
      console.error('Error counting total questions:', totalError);
      return { error: totalError.message };
    }

    return {
      sources: sources || [],
      total: totalCount || 0
    };
  } catch (error) {
    console.error('Error in countQuestionsBySource:', error);
    return { error: 'An unexpected error occurred' };
  }
};

export const deleteAllQuestionsWithSource = async (source: string) => {
  try {
    // First count how many will be deleted
    const { count, error: countError } = await supabase
      .from('questions')
      .select('*', { count: 'exact' })
      .eq('Source', source);

    if (countError) {
      console.error('Error counting questions to delete:', countError);
      return { error: countError.message };
    }

    // Then delete them
    const { error } = await supabase
      .from('questions')
      .delete()
      .eq('Source', source);

    if (error) {
      console.error(`Error deleting questions with source ${source}:`, error);
      return { error: error.message };
    }

    return { count };
  } catch (error) {
    console.error('Error in deleteAllQuestionsWithSource:', error);
    return { error: 'An unexpected error occurred' };
  }
};

export const deleteAllQuestions = async () => {
  try {
    // First count how many will be deleted
    const { count, error: countError } = await supabase
      .from('questions')
      .select('*', { count: 'exact' });

    if (countError) {
      console.error('Error counting all questions:', countError);
      return { error: countError.message };
    }

    // Then delete them all
    const { error } = await supabase
      .from('questions')
      .delete()
      .neq('ID', -1); // This is a trick to delete all rows

    if (error) {
      console.error('Error deleting all questions:', error);
      return { error: error.message };
    }

    return { count };
  } catch (error) {
    console.error('Error in deleteAllQuestions:', error);
    return { error: 'An unexpected error occurred' };
  }
};
